import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Rating,
  useTheme,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAgentReport } from '../../contexts/AgentReportContext';

// Import components
import BasicDetails from './agent_report/agent_report_details/BasicDetails';
import Performance from './agent_report/agent_report_details/Performance';
import RecentActivities from './agent_report/agent_report_details/RecentActivities';
import SiteVisits from './agent_report/agent_report_details/SiteVisits';
import Bookings from './agent_report/agent_report_details/Bookings';
import AIInsights from './agent_report/agent_report_details/AIInsights';
import Attendance from './agent_report/agent_report_details/Attendance';
import Location from './agent_report/agent_report_details/Location';
import Tasks from './agent_report/agent_report_details/Tasks';

const AgentReportDetail = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { agentId } = useParams();
  console.log('agentId', agentId);
  const { fetchSingleAgentDetails } = useAgentReport();
  
  const [tabValue, setTabValue] = useState(0);
  // API data states
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch agent data on component mount
  useEffect(() => {
    if (!agentId || isNaN(parseInt(agentId))) {
      setError(new Error('Invalid agent ID'));
      setLoading(false);
      return;
    }
    
    const fetchAgentData = async () => {
      console.log('AgentReportDetail - fetching agent data for:', agentId);
      setLoading(true);
      setError(null);
      
      try {
        // Fetch agent details only
        const details = await fetchSingleAgentDetails(agentId);
        if (details) {
          setAgentData(details);
          console.log('AgentReportDetail - agent data fetched successfully');
        }
      } catch (err) {
        setError(err);
        console.error('Error fetching agent data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAgentData();
  }, [agentId]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography color="error">Error loading agent data: {error.message}</Typography>
      </Box>
    );
  }

  // No agent data
  if (!agentData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Agent not found</Typography>
      </Box>
    );
  }


  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleExportPDF = () => {
    // Create a new window for PDF generation
    const printWindow = window.open('', '_blank');
    
    // Get the current tab content
    const currentTabContent = document.querySelector('[role="tabpanel"]');
    const agentInfoCard = document.querySelector('[data-testid="agent-info-card"]') || 
                         document.querySelector('.MuiPaper-root');
    
    // Generate PDF content
    const pdfContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Agent Performance Report - ${agentData.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .agent-info { background: #f5f5f5; padding: 20px; border-radius: 8px; margin-bottom: 20px; }
            .metrics-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 15px; margin-bottom: 20px; }
            .metric-card { background: white; padding: 15px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
            .metric-title { font-size: 14px; color: #666; margin-bottom: 5px; }
            .metric-value { font-size: 24px; font-weight: bold; color: #333; }
            .metric-subtitle { font-size: 12px; color: #999; }
            .section-title { font-size: 18px; font-weight: bold; margin: 20px 0 10px 0; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Agent Performance Report</h1>
            <h2>${agentData.name}</h2>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="agent-info">
            <h3>Agent Information</h3>
            <p><strong>Name:</strong> ${agentData.name}</p>
            <p><strong>Email:</strong> ${agentData.email || 'N/A'}</p>
            <p><strong>Phone:</strong> ${agentData.phone || 'N/A'}</p>
            <p><strong>Status:</strong> ${agentData.status || 'Active'}</p>
            <p><strong>Joined:</strong> ${agentData.join_date || 'N/A'}</p>
            <p><strong>Last Active:</strong> ${agentData.last_active_at || 'Not specified'}</p>
            ${agentData.team ? `<p><strong>Team:</strong> ${agentData.team}</p>` : ''}
            <p><strong>Team Rank:</strong> ${agentData.team_rank || 'N/A'}</p>
          </div>
          
          <div class="section-title">Performance Overview</div>
          <div class="metrics-grid">
            <div class="metric-card">
              <div class="metric-title">Total Leads Assigned</div>
              <div class="metric-value">156</div>
              <div class="metric-subtitle">This Month</div>
            </div>
            <div class="metric-card">
              <div class="metric-title">Total Contacted</div>
              <div class="metric-value">142</div>
              <div class="metric-subtitle">91% Contact Rate</div>
            </div>
            <div class="metric-card">
              <div class="metric-title">Site Visits Done</div>
              <div class="metric-value">28</div>
              <div class="metric-subtitle">Completed Visits</div>
            </div>
            <div class="metric-card">
              <div class="metric-title">Response Time</div>
              <div class="metric-value">18 min</div>
              <div class="metric-subtitle">Avg Response</div>
            </div>
            <div class="metric-card">
              <div class="metric-title">Active Leads (in Progress)</div>
              <div class="metric-value">67</div>
              <div class="metric-subtitle">Currently Working</div>
            </div>
            <div class="metric-card">
              <div class="metric-title">Pipeline Value</div>
              <div class="metric-value">₹2.4 Cr</div>
              <div class="metric-subtitle">Active Pipeline</div>
            </div>
          </div>
          
          <div class="section-title">Productivity Metrics</div>
          <div class="metrics-grid">
            <div class="metric-card">
              <div class="metric-title">Daily Call Volume</div>
              <div class="metric-value">0</div>
              <div class="metric-subtitle">Calls per Day</div>
            </div>
            <div class="metric-card">
              <div class="metric-title">Time Distribution</div>
              <div class="metric-value">New: 40% | Follow-up: 35% | Closure: 25%</div>
              <div class="metric-subtitle">Activity Breakdown</div>
            </div>
            <div class="metric-card">
              <div class="metric-title">Task Completion</div>
              <div class="metric-value">94%</div>
              <div class="metric-subtitle">Tasks Completed</div>
            </div>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(pdfContent);
    printWindow.document.close();
    
    // Wait for content to load, then trigger print
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };


  return (
    <Box sx={{ flexGrow: 1, py: 3, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/reports/agents')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                {agentData.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Agent Performance Details
              </Typography>
            </Box>
          </Box>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPDF}
            sx={{
              backgroundColor: '#d32f2f',
              '&:hover': {
                backgroundColor: '#b71c1c',
              },
            }}
          >
            Export PDF
          </Button>
        </Stack>
      </Box>

      {/* Agent Info Card */}
      <BasicDetails agentId={agentId} />

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
            }
          }}
        >
          <Tab label="Performance Report" />
          <Tab label="Leads & Visits" />
          <Tab label="Bookings" />
          <Tab label="Attendance" />
          {agentData?.ai_insights_available && (
            <Tab label="AI Insights" />
          )}
          <Tab label="Recent Calls" />
          <Tab label="Tasks" />
          <Tab label="Location" />
        </Tabs>
      </Box>
      {/* Tab Content */}
      {tabValue === 0 && (
        <Performance 
          agentId={agentId}
        />
      )}

      {tabValue === 1 && (
        <SiteVisits 
          agentId={agentId}
        />
      )}

      {tabValue === 2 && (
        <Bookings 
          agentId={agentId}
        />
      )}

      {tabValue === 3 && (
        <Attendance 
          agentId={agentId}
        />
      )}

      {/* AI Insights tab - only show if available and tab index matches */}
      {agentData?.ai_insights_available && tabValue === 4 && (
        <AIInsights 
          agentId={agentId}
        />
      )}

      {/* Recent Calls tab - adjust index based on AI Insights availability */}
      {tabValue === (agentData?.ai_insights_available ? 5 : 4) && (
        <RecentActivities 
          agentId={agentId}
        />
      )}

      {/* Tasks tab - adjust index based on AI Insights availability */}
      {tabValue === (agentData?.ai_insights_available ? 6 : 5) && (
        <Tasks 
          agentId={agentId}
        />
      )}

      {/* Location tab - always last */}
      {tabValue === (agentData?.ai_insights_available ? 7 : 6) && (
        <Location 
          agentId={agentId}
        />
      )}
    </Box>
  );
};

export default AgentReportDetail; 